<template>
  <div v-if="monthGroups.length" class="daily-balance-calendar">
    <div class="balance-month-selector">
      <button
        type="button"
        class="month-nav-btn"
        :disabled="selectedMonthIndex === 0"
        @click="goToPreviousMonth"
      >
        <i class="month-nav-btn-icon las la-angle-left" />
      </button>

      <ChatbotSelect
        v-model="selectedMonthIndex"
        :options="monthOptions"
        :reduce="option => option.value"
        label="label"
        class="month-select"
      />

      <button
        type="button"
        class="month-nav-btn"
        :disabled="selectedMonthIndex >= monthGroups.length - 1"
        @click="goToNextMonth"
      >
        <i class="month-nav-btn-icon las la-angle-right" />
      </button>
    </div>

    <div v-if="selectedMonthCalendar" class="balance-month">
      <div class="calendar-weekdays">
        <span
          v-for="weekday in calendarWeekdays"
          :key="weekday"
          class="calendar-weekday"
        >
          {{ weekday }}
        </span>
      </div>

      <div class="balance-calendar">
        <div
          v-for="(cell, index) in selectedMonthCalendar.cells"
          :key="`${selectedMonthCalendar.key}-${index}`"
          class="balance-cell"
          :class="{
            'is-empty': cell.isEmpty,
            'has-balance': cell.hasBalance
          }"
        >
          <template v-if="!cell.isEmpty">
            <span class="balance-date">{{ cell.day }}</span>
            <span
              v-if="cell.amount !== null"
              class="balance-value"
            >
              {{ formatBalance(cell.amount) }}
            </span>
            <span v-else class="balance-value balance-value-empty">—</span>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ChatbotSelect from '../ChatbotSelect.vue';

export default {
  name: 'DailyBalanceCalendar',
  components: {
    ChatbotSelect
  },
  props: {
    rows: {
      type: Array,
      default: () => []
    },
    currency: {
      type: String,
      default: 'AED'
    }
  },
  data() {
    return {
      selectedMonthIndex: 0
    };
  },
  watch: {
    monthGroups: {
      immediate: true,
      handler(groups, previousGroups) {
        if (!groups.length) {
          this.selectedMonthIndex = 0;
          return;
        }

        const isInitialLoad = !previousGroups || !previousGroups.length;
        if (isInitialLoad || this.selectedMonthIndex >= groups.length) {
          this.selectedMonthIndex = groups.length - 1;
        }
      }
    }
  },
  computed: {
    calendarWeekdays() {
      return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    },

    monthGroups() {
      const groups = {};

      this.rows.forEach((row) => {
        const date = new Date(row.date);
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

        if (!groups[key]) {
          groups[key] = {
            key,
            label: date.toLocaleDateString('en-AE', { month: 'long', year: 'numeric' }),
            rows: []
          };
        }

        groups[key].rows.push(row);
      });

      return Object.values(groups).sort((a, b) => a.key.localeCompare(b.key));
    },

    monthOptions() {
      return this.monthGroups.map((group, index) => ({
        id: group.key,
        label: group.label,
        value: index
      }));
    },

    selectedMonthGroup() {
      return this.monthGroups[this.selectedMonthIndex] || null;
    },

    selectedMonthCalendar() {
      const group = this.selectedMonthGroup;
      if (!group) return null;

      const [year, month] = group.key.split('-').map(Number);
      const firstDay = new Date(year, month - 1, 1);
      const daysInMonth = new Date(year, month, 0).getDate();
      const startWeekday = firstDay.getDay();
      const balanceByDate = group.rows.reduce((map, row) => {
        map[row.date] = row.amount;
        return map;
      }, {});

      const cells = [];

      for (let index = 0; index < startWeekday; index += 1) {
        cells.push({ isEmpty: true });
      }

      for (let day = 1; day <= daysInMonth; day += 1) {
        const date = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        const amount = Object.prototype.hasOwnProperty.call(balanceByDate, date)
          ? balanceByDate[date]
          : null;

        cells.push({
          isEmpty: false,
          day,
          date,
          amount,
          hasBalance: amount !== null && amount !== 0
        });
      }

      while (cells.length % 7 !== 0) {
        cells.push({ isEmpty: true });
      }

      return {
        key: group.key,
        label: group.label,
        cells
      };
    }
  },
  methods: {
    formatBalance(balance) {
      const amount = Number(balance);
      if (!Number.isFinite(amount)) return 'N/A';

      return new Intl.NumberFormat('en-AE', {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    },

    goToPreviousMonth() {
      if (this.selectedMonthIndex > 0) {
        this.selectedMonthIndex -= 1;
      }
    },

    goToNextMonth() {
      if (this.selectedMonthIndex < this.monthGroups.length - 1) {
        this.selectedMonthIndex += 1;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.daily-balance-calendar {
  margin-top: 0.25rem;
  font-family: "Quicksand";
}

.balance-month-selector {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.month-nav-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  transition: color 0.15s ease;

  &:hover:not(:disabled) .month-nav-btn-icon {
    color: #111827;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  i {
    font-size: 1.25rem;
    line-height: 1;
  }
}

.month-select {
  min-width: 15rem;
  max-width: 15rem;
}

.month-select ::v-deep .cselect__trigger {
  min-height: auto;
  padding: 0.25rem 0.5rem;
  border: none;
  background: transparent;
  box-shadow: none;
  justify-content: center;
  font-size: 0.935rem;
}

.month-select ::v-deep .cselect__value {
  text-align: center;
}

.month-select ::v-deep .cselect__icon {
  margin-left: 0.25rem;
}

.month-select ::v-deep .cselect__menu {
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.month-select ::v-deep .cselect__options {
  padding: 0.25rem 0;
}

.month-select ::v-deep .cselect__option {
  justify-content: center;
  padding: 0.5rem 0.75rem;
  font-size: 0.9rem;
}

.calendar-weekdays {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.calendar-weekday {
  text-align: center;
  color: #64748b;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.balance-calendar {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 0.5rem;
}

.balance-cell {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.55rem 0.65rem;
  min-width: 0;
  min-height: 3.75rem;
  border: 1px solid #f1f5f9;
  border-radius: 0.375rem;
  background: #ffffff;

  &.is-empty {
    border-color: transparent;
    background: transparent;
  }
}

.balance-date {
  color: #64748b;
  font-size: 0.792rem;
  letter-spacing: 0.03em;
}

.balance-value {
  color: #334155;
  font-size: 0.85rem;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.balance-value-empty {
  color: #cbd5e1;
}

.balance-cell.has-balance .balance-value {
  color: #166534;
}
</style>
